import React, { useEffect, useMemo, useState } from "react";
import { CalendarDays, Save, Target, UserCheck, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { api, errMsg, fmtINR } from "../lib/api";
import { staffLabelText } from "./StaffAvatar";
import { monthLabel } from "../lib/dateRange";

const num = (s) => (s === "" || s == null ? null : Number(s));
const str = (v) => (v == null ? "" : String(v));

/**
 * Phase 175 — when the selected range spans several months, targets are still stored per month: this dialog
 * lists every month touched by the range with its own input(s) and shows the pro-rated range total live.
 *
 * kind = "supervisor" → Personal target + Team goal per month (PUT /supervisor-targets/{id} per changed month)
 * kind = "collector"  → Target per month                      (PUT /collector-targets/bulk per changed month)
 * `row` is the table row from the list API (carries `months: [{month, label, fraction, days_in_range, days_in_month, …}]`).
 */
export const MonthlyTargetsDialog = ({ code, kind, row, rangeLabel, open, onOpenChange, onSaved }) => {
  const months = useMemo(() => row?.months || [], [row]);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);
  const isSup = kind === "supervisor";

  useEffect(() => {
    if (!open || !row) return;
    const e = {};
    months.forEach((m) => {
      e[m.month] = isSup ? { personal_target: str(m.personal_target), team_goal: str(m.team_goal) } : { target: str(m.target) };
    });
    setEdits(e);
  }, [open, row, months, isSup]);

  const changed = useMemo(() => months.filter((m) => {
    const e = edits[m.month] || {};
    return isSup
      ? (e.personal_target ?? "") !== str(m.personal_target) || (e.team_goal ?? "") !== str(m.team_goal)
      : (e.target ?? "") !== str(m.target);
  }), [months, edits, isSup]);

  // live pro-rated totals from the draft values
  const prorated = (field) => {
    let any = false, sum = 0;
    months.forEach((m) => {
      const v = num((edits[m.month] || {})[field]);
      if (v != null && !Number.isNaN(v)) { any = true; sum += v * m.fraction; }
    });
    return any ? Math.round(sum * 100) / 100 : null;
  };
  const setField = (month, field, value) => setEdits((e) => ({ ...e, [month]: { ...(e[month] || {}), [field]: value } }));

  const save = async () => {
    if (!changed.length) return;
    setBusy(true);
    try {
      for (const m of changed) {
        const e = edits[m.month] || {};
        if (isSup) {
          await api.put(`/t/${code}/supervisor-targets/${row.supervisor_id}`, {
            month: m.month, personal_target: num(e.personal_target), team_goal: num(e.team_goal),
          });
        } else {
          await api.put(`/t/${code}/collector-targets/bulk`, { month: m.month, items: [{ collector_id: row.id, target_amount: num(e.target) }] });
        }
      }
      toast.success(`Targets saved for ${isSup ? row.supervisor_name : row.name}`, {
        description: `${changed.length} month${changed.length === 1 ? "" : "s"} updated: ${changed.map((m) => monthLabel(m.month, true)).join(", ")}`,
      });
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const name = isSup ? row?.supervisor_name : row?.name;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-3xl" data-testid="monthly-targets-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-base">
            <Target className="h-4 w-4" /> Monthly targets — {row?.employee_id ? staffLabelText(name, row.employee_id) : name}
          </DialogTitle>
          <DialogDescription>
            Targets are set <b>per calendar month</b>. The selected range <b>{rangeLabel}</b> touches {months.length} month{months.length === 1 ? "" : "s"};
            each month counts towards the range in proportion to its days inside it. Blank or 0 clears a target.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border overflow-x-auto">
          <Table data-testid="monthly-targets-table">
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Month</TableHead>
                <TableHead className="text-xs">In range</TableHead>
                {isSup ? (
                  <>
                    <TableHead className="text-xs min-w-[150px]"><span className="inline-flex items-center gap-1"><UserCheck className="h-3 w-3" /> Personal target (₹)</span></TableHead>
                    <TableHead className="text-xs min-w-[150px]"><span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> Team goal (₹)</span></TableHead>
                  </>
                ) : (
                  <TableHead className="text-xs min-w-[160px]">Target (₹)</TableHead>
                )}
                <TableHead className="text-xs text-right whitespace-nowrap">Counts in range</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {months.map((m) => {
                const e = edits[m.month] || {};
                const pct = Math.round(m.fraction * 100);
                const contrib = isSup
                  ? [num(e.personal_target), num(e.team_goal)].map((v) => (v == null || Number.isNaN(v) ? null : Math.round(v * m.fraction * 100) / 100))
                  : [num(e.target)].map((v) => (v == null || Number.isNaN(v) ? null : Math.round(v * m.fraction * 100) / 100));
                return (
                  <TableRow key={m.month} data-testid={`monthly-targets-row-${m.month}`}>
                    <TableCell className="py-2 text-sm font-medium whitespace-nowrap">
                      <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5 text-muted-foreground" /> {monthLabel(m.month, true)}</span>
                    </TableCell>
                    <TableCell className="py-2 text-xs text-muted-foreground whitespace-nowrap">
                      {m.partial ? `${m.days_in_range} of ${m.days_in_month} days · ${pct}%` : "whole month"}
                    </TableCell>
                    {isSup ? (
                      <>
                        <TableCell className="py-2">
                          <Input type="number" min="0" step="100" value={e.personal_target ?? ""} placeholder="—" className="h-9"
                            onChange={(ev) => setField(m.month, "personal_target", ev.target.value)} data-testid={`monthly-targets-personal-${m.month}`} />
                        </TableCell>
                        <TableCell className="py-2">
                          <Input type="number" min="0" step="1000" value={e.team_goal ?? ""} placeholder="—" className="h-9"
                            onChange={(ev) => setField(m.month, "team_goal", ev.target.value)} data-testid={`monthly-targets-goal-${m.month}`} />
                        </TableCell>
                      </>
                    ) : (
                      <TableCell className="py-2">
                        <Input type="number" min="0" step="100" value={e.target ?? ""} placeholder="—" className="h-9"
                          onChange={(ev) => setField(m.month, "target", ev.target.value)} data-testid={`monthly-targets-target-${m.month}`} />
                      </TableCell>
                    )}
                    <TableCell className="py-2 text-xs text-right tabular-nums text-muted-foreground whitespace-nowrap">
                      {isSup
                        ? <>{contrib[0] != null ? fmtINR(contrib[0]) : "—"} <span className="opacity-60">/</span> {contrib[1] != null ? fmtINR(contrib[1]) : "—"}</>
                        : (contrib[0] != null ? fmtINR(contrib[0]) : "—")}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 text-xs" data-testid="monthly-targets-footer">
          <p className="text-muted-foreground">
            Range total (pro-rated):{" "}
            {isSup ? (
              <>
                <span className="font-medium text-foreground tabular-nums" data-testid="monthly-targets-total-personal">{prorated("personal_target") != null ? fmtINR(prorated("personal_target")) : "no personal target"}</span>
                {" · "}
                <span className="font-medium text-foreground tabular-nums" data-testid="monthly-targets-total-goal">{prorated("team_goal") != null ? fmtINR(prorated("team_goal")) : "no team goal"}</span>
              </>
            ) : (
              <span className="font-medium text-foreground tabular-nums" data-testid="monthly-targets-total">{prorated("target") != null ? fmtINR(prorated("target")) : "no target"}</span>
            )}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={busy} data-testid="monthly-targets-cancel">Cancel</Button>
            <Button size="sm" onClick={save} disabled={busy || changed.length === 0} data-testid="monthly-targets-save">
              <Save className="h-4 w-4 mr-1.5" /> {busy ? "Saving..." : changed.length ? `Save ${changed.length} month${changed.length === 1 ? "" : "s"}` : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
